import * as qs from 'qs';
import { AxiosResponse } from 'axios';

import { RoleEnum } from '@project/enums/role.enum';
import { authNetworkService } from '@project/services';
import {
  CanLoginDto,
  Guid,
  PaginatedList,
  ReportFilterProps,
  ServiceResult,
  User,
} from '@project/types';

import userUrls from './user.urls';

const generateHeaders = () => {
  return {
    Accept: 'application/csv',
  };
};

const generateParams = (filterProps?: ReportFilterProps) => {
  return {
    fromDate: filterProps.fromDate,
    toDate: filterProps.toDate,
    viewAll: filterProps.viewAll,
    attributes: ['IdNumber', 'BrnNumber', 'PhoneNumber', 'WhatsAppVerified'],
    isUnverified: !filterProps.verified,
    sentNotification: filterProps.sentNotification,
  };
};

const get = async (pageNumber = 1, pageSize = 10, role: RoleEnum = RoleEnum.CallCenterAgent) => {
  const url = userUrls.getBaseUrl();

  const response = await authNetworkService
    .get<PaginatedList<User>>(url, {
      params: { pageNumber, pageSize, role },
      paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
    })
    .catch((error) => {
      return Promise.reject(error);
    });
  return response.data;
};

const deletePortalUserAccount = (userId: Guid) => {
  const url = userUrls.getUserResourceUrl(userId);

  return authNetworkService.delete(url).catch((error) => {
    return Promise.reject(error);
  });
};

export const getUnverifiedEmailsReport = (
  filterProps?: ReportFilterProps,
): Promise<AxiosResponse<Blob>> => {
  const url = userUrls.baseContentUrl();

  return authNetworkService.get(url, {
    responseType: 'blob',
    timeout: 0,
    headers: { Accept: 'application/csv' },
    params: {
      fromDate: filterProps.fromDate,
      toDate: filterProps.toDate,
      attributes: ['Email', 'CreatedDate', 'PhoneNumber', 'FullName'],
      isUnverified: true,
    },
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
  });
};

export const getArchivedAccountsReport = (
  filterProps?: ReportFilterProps,
): Promise<AxiosResponse<Blob>> => {
  const url = userUrls.getArchivedAccountUrl();

  return authNetworkService.get(url, {
    responseType: 'blob',
    timeout: 0,
    headers: { Accept: 'application/csv' },
    params: {
      fromDate: filterProps?.fromDate,
      toDate: filterProps?.toDate,
    },
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
  });
};

export const getUsersPhoneNumbers = (
  filterProps?: ReportFilterProps,
): Promise<AxiosResponse<Blob>> => {
  const url = userUrls.baseContentUrl();
  const headers = generateHeaders();
  const parameters = generateParams(filterProps);

  return authNetworkService.get(url, {
    responseType: 'blob',
    timeout: 0,
    headers,
    params: parameters,
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
  });
};

export const getUsersIdsAndBrns = (
  filterProps?: ReportFilterProps,
): Promise<AxiosResponse<Blob>> => {
  const url = userUrls.baseContentUrl();
  const headers = generateHeaders();

  return authNetworkService.get(url, {
    responseType: 'blob',
    timeout: 0,
    headers,
    params: {
      fromDate: filterProps.fromDate,
      toDate: filterProps.toDate,
      attributes: ['IdNumber', 'BrnNumber', 'TrnNumber'],
    },
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
  });
};

export const getUsersProxyIdsWithNoBrns = (
  filterProps?: ReportFilterProps,
): Promise<AxiosResponse<Blob>> => {
  const url = userUrls.baseContentUrl();
  const headers = generateHeaders();

  return authNetworkService.get(url, {
    responseType: 'blob',
    timeout: 0,
    headers,
    params: {
      fromDate: filterProps.fromDate,
      toDate: filterProps.toDate,
      attributes: ['ProxyId', 'NoBrnNumber'],
    },
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
  });
};

export const getUsersOtpOrigination = (
  filterProps?: ReportFilterProps,
): Promise<AxiosResponse<Blob>> => {
  const url = userUrls.baseContentUrl();
  const headers = generateHeaders();

  return authNetworkService.get(url, {
    responseType: 'blob',
    timeout: 0,
    headers,
    params: {
      fromDate: filterProps.fromDate,
      toDate: filterProps.toDate,
      attributes: ['User', 'Otp'],
    },
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
  });
};

export const getAartoSignUps = (filterProps?: ReportFilterProps): Promise<AxiosResponse<Blob>> => {
  const url = userUrls.baseContentUrl();
  const headers = generateHeaders();

  return authNetworkService.get(url, {
    responseType: 'blob',
    timeout: 0,
    headers,
    params: {
      fromDate: filterProps.fromDate,
      toDate: filterProps.toDate,
      attributes: ['Sign-Ups', 'Aarto'],
    },
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
  });
};

export const getUserProfiles = (): Promise<AxiosResponse<Blob>> => {
  const url = userUrls.baseContentUrl();

  return authNetworkService.get(url, {
    responseType: 'blob',
    timeout: 0,
    headers: { Accept: 'application/csv' },
    params: {
      attributes: ['Identifier', 'Email', 'ProfileType'],
    },
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
  });
};

const updateIsWhatsAppVerified = (userId: string, isWhatsAppVerified: boolean): Promise<void> => {
  const url = `${userUrls.getUserResourceUrl(userId)}/IsWhatsAppVerified`;

  return authNetworkService.put(url, { isVerified: isWhatsAppVerified });
};

export const upsertExtension = async (
  userId: string,
  extension: string,
): Promise<{ userId: string; extension: string }> => {
  const url = userUrls.extensionUrl(userId);

  await authNetworkService.put(url, { extension });
  return { userId, extension };
};

export const deleteExtension = async (userId: string): Promise<{ userId: string }> => {
  const url = userUrls.extensionUrl(userId);

  await authNetworkService.delete(url);
  return { userId };
};

const canLogin = async (userId: string): Promise<ServiceResult<CanLoginDto>> => {
  const url = userUrls.getUserCanLogin(userId);

  const response = await authNetworkService.get<ServiceResult<CanLoginDto>>(url).catch((error) => {
    return Promise.reject(error);
  });

  return response.data;
};

export default {
  get,
  canLogin,
  deleteExtension,
  upsertExtension,
  getUserProfiles,
  getAartoSignUps,
  getUsersIdsAndBrns,
  getUsersPhoneNumbers,
  getUsersOtpOrigination,
  deletePortalUserAccount,
  getArchivedAccountsReport,
  updateIsWhatsAppVerified,
  getUnverifiedEmailsReport,
  getUsersProxyIdsWithNoBrns,
};
